import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermisos } from '../../context/PermisosContext';
import { Search } from 'lucide-react';
import { getApiUrl } from '../../utils/apiConfig';
import EditAdministracionContent from './EditAdministracionContent';
import NewAdministracionContent from './NewAdministracionContent';

const AdminContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, loadingPermisos } = usePermisos();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [permisos, setPermisos] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isAdmin && !loadingPermisos) {
      fetchPermisos();
    }
  }, [isAdmin, loadingPermisos]);

  const fetchPermisos = async () => {
    try {
      setLoadingData(true);
      const params = new URLSearchParams();
      params.append('pageSize', '1000');
      const response = await fetch(getApiUrl(`/api/permisosAdmin?${params}`), {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPermisos(data);
      } else {
        console.error('Error al cargar permisos');
      }
    } catch (error) {
      console.error('Error al cargar permisos:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Abrir creación / edición mediante query-string
  const openNew = () => setSearchParams({ new: '1' });
  const openEdit = (email) => {
    if (!email) return;
    setSearchParams({ edit: email });
  };

  // Filtrar permisos localmente
  const filteredPermisos = permisos.filter(permiso => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const allValues = Object.values(permiso).join(' ').toLowerCase();
    return allValues.includes(search);
  });

  const filtrosActivos = searchTerm.trim() !== '';
  const limpiarFiltros = () => {
    setSearchTerm('');
    setCurrentPage(0);
  };

  // Paginación local sobre datos filtrados
  const pageCount = Math.max(1, Math.ceil(filteredPermisos.length / itemsPerPage));
  const offset = currentPage * itemsPerPage;
  const currentPermisos = filteredPermisos.slice(offset, offset + itemsPerPage);

  // 🔐 Si el usuario NO es admin (DESPUÉS de cargar permisos)
  if (!isAdmin && !loadingPermisos) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="text-center border rounded-xl p-8">
          <div className="mb-4">
            <svg className="w-20 h-20 mx-auto text-red-400 sm:w-24 sm:h-24" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Acceso Denegado</h2>
          <p className="text-gray-600 mb-2 text-sm">
            Tu usuario no tiene permisos para acceder a la sección de <strong>Administración</strong>.
          </p>
          <p className="text-xs text-gray-500 mb-6">
            Esta sección solo está disponible para administradores del sistema.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-3 py-1.5 rounded-lg text-red-600 border bg-red-50 hover:bg-red-100 border-red-200 text-sm font-medium transition-colors duration-200"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Si está cargando permisos, mostrar loading
  if (loadingPermisos || loadingData) {
    return (
      <div className="flex flex-col justify-center items-center py-32">
        <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin mb-4"></div>
        <p className="text-gray-600">Cargando datos de permisos...</p>
      </div>
    );
  }

  // Rutas internas por querystring
  const isEditing = !!searchParams.get('edit');
  const isCreating = !!searchParams.get('new');

  if (isEditing) {
    return (
      <EditAdministracionContent
        onSave={() => {
          fetchPermisos();
          setSearchParams({});
        }}
        onCancel={() => setSearchParams({})}
      />
    );
  }

  if (isCreating) {
    return (
      <NewAdministracionContent
        onSave={() => {
          fetchPermisos();
          setSearchParams({});
        }}
        onCancel={() => setSearchParams({})}
      />
    );
  }

  // Vista principal
  return (
    <div className="p-4 sm:p-6 lg:p-6 bg-gray-50 no-select min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 my-4 sm:gap-0 sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Administración</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Gestión de permisos de comercial</p>
          </div>
          <div className="flex items-center gap-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-sm border hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-800 text-xs sm:text-sm md:text-base truncate max-w-[100px] sm:max-w-[150px] md:max-w-none">
                {user?.name || 'Usuario'}
              </p>
            </div>
          </div>
        </div>

        {/* Filtros y acciones */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-4 my-4">
          <div className="flex flex-col gap-4">
            {/* Filtros principales */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 items-stretch sm:items-end flex-1">
              {/* Búsqueda */}
              <div className="flex flex-col w-full sm:w-auto">
                <label className="text-sm font-medium text-gray-700 mb-1">Buscar</label>
                <div className="flex w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Buscar en todas las columnas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="px-3 sm:px-4 py-2 border border-gray-200 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 flex-1 sm:flex-none border-r-0 text-sm"
                  />
                  <button
                    onClick={handleSearch}
                    className="px-2 sm:px-3 py-1.5 bg-blue-600 text-white rounded-r-xl hover:bg-blue-700 transition-all duration-200 shadow-sm border border-blue-600 border-l-0 flex-shrink-0"
                    title="Buscar"
                  >
                    <Search size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </button>
                </div>
              </div>

              {/* Contador y Limpiar filtros en la misma fila en móvil */}
              <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4 w-full sm:w-auto sm:ml-auto">
                {filtrosActivos && (
                  <button
                    onClick={limpiarFiltros}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-all duration-200 border border-red-200 text-sm"
                    title="Limpiar filtros"
                  >
                    Limpiar filtros
                  </button>
                )}
                {/* Contador de resultados */}
                <span className={`px-3 py-2 bg-green-100 text-green-800 rounded-xl text-sm font-semibold border border-green-200 text-center ${!filtrosActivos ? 'col-span-2 sm:col-span-1' : ''}`}>
                  {filteredPermisos.length} Comerciales
                </span>
                {/* Botón nuevo comercial */}
                <button
                  onClick={openNew}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all duration-200 shadow-sm border border-blue-600 w-full sm:w-auto"
                  title="Crear nuevo comercial"
                >
                  + Nuevo Comercial
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de administración */}
        <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {isMobile ? (
            <div className="p-3 sm:p-4 md:p-6">
              <div className="flex flex-col gap-3 sm:gap-4">
                {currentPermisos.map((permiso, index) => (
                  <div
                    key={permiso.EmailUser || index}
                    className="group bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-red-200 transition-all duration-300 cursor-pointer"
                    onClick={() => openEdit(permiso.EmailUser)}
                  >
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className="p-2 sm:p-3 bg-red-50 rounded-lg sm:rounded-xl group-hover:bg-red-100 transition-colors flex-shrink-0">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-800 text-base sm:text-lg truncate">{permiso.EmailUser}</h3>
                          <p className="text-red-600 font-medium text-sm truncate">{permiso.FuncionComercial || '-'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:gap-3">
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">Comerciales:</span>
                        <span className="font-medium text-gray-800 text-sm truncate text-right">{permiso.C_Comerciales || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">Bloques:</span>
                        <span className="font-medium text-gray-800 text-sm truncate text-right">{permiso.O_Bloques || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">Ubicaciones:</span>
                        <span className="font-medium text-gray-800 text-sm truncate text-right">{permiso.O_Ubicaciones || '-'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-[900px] table-auto w-full">
                <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                  <tr className="bg-gradient-to-r from-red-50 to-red-100 border-b-2 border-red-200">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 text-sm w-1/5">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 text-sm w-1/5">Función Comercial</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 text-sm w-1/5">Comerciales</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 text-sm w-1/5">Bloques</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 text-sm w-1/5">Ubicaciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentPermisos.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        {filtrosActivos ? 'No se encontraron permisos con ese criterio' : 'No hay permisos registrados'}
                      </td>
                    </tr>
                  ) : (
                    currentPermisos.map((permiso, index) => (
                      <tr
                        key={permiso.EmailUser || index}
                        onClick={() => openEdit(permiso.EmailUser)}
                        className={`hover:bg-red-50 transition-colors duration-200 cursor-pointer ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-800 w-1/5">
                          <div className="truncate max-w-full" title={permiso.EmailUser}>
                            {permiso.EmailUser}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-200 text-sm text-gray-700 w-1/5">
                          <div className="truncate max-w-full" title={permiso.FuncionComercial || ''}>
                            {permiso.FuncionComercial || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-200 text-sm text-gray-700 w-1/5">
                          <div className="truncate max-w-full" title={permiso.C_Comerciales || ''}>
                            {permiso.C_Comerciales || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-200 text-sm text-gray-700 w-1/5">
                          <div className="truncate max-w-full" title={permiso.O_Bloques || ''}>
                            {permiso.O_Bloques || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-200 text-sm text-gray-700 w-1/5">
                          <div className="truncate max-w-full" title={permiso.O_Ubicaciones || ''}>
                            {permiso.O_Ubicaciones || '-'}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center sm:gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3 p-3 md:p-0">
              <label className="text-xs sm:text-sm font-medium text-gray-700">Items por página:</label>
              <div className="flex items-center gap-1">
                {[5, 10, 20].map(size => (
                  <button
                    key={size}
                    onClick={() => {
                      setItemsPerPage(size);
                      setCurrentPage(0);
                    }}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
                      itemsPerPage === size
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-red-50 hover:border-red-300'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 p-1 md:p-0">
              <button 
                onClick={() => setCurrentPage(0)} 
                disabled={currentPage === 0} 
                className="p-1.5 sm:p-2 border border-gray-200 rounded-lg sm:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm"
              >
                <span>{'<<'}</span>
              </button>
              <button 
                onClick={() => setCurrentPage((prev) => prev - 1)} 
                disabled={currentPage === 0} 
                className="p-1.5 sm:p-2 border border-gray-200 rounded-lg sm:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm"
              >
                <span>{'<'}</span>
              </button>
              <span className="px-2 sm:px-4 py-1.5 sm:py-2 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-red-800">
                {currentPage + 1} / {pageCount}
              </span>
              <button 
                onClick={() => setCurrentPage((prev) => prev + 1)} 
                disabled={currentPage >= pageCount - 1} 
                className="p-1.5 sm:p-2 border border-gray-200 rounded-lg sm:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm"
              >
                <span>{'>'}</span>
              </button>
              <button 
                onClick={() => setCurrentPage(pageCount - 1)} 
                disabled={currentPage >= pageCount - 1} 
                className="p-1.5 sm:p-2 border border-gray-200 rounded-lg sm:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm"
              >
                <span>{'>>'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContent;
